/**
 * positionShares.js — the user's QB/RB/WR/TE price dials, as a pure function.
 *
 * Four sliders that say "quarterbacks go for more in my league" without
 * importing anything. Each is a multiplier on that position's prices, riding
 * the same rail league-history calibration uses: usePricing hangs the vector on
 * the price map as `__leagueMultipliers`, and auction.js reads it as `posMult`
 * to scale tierFloor/tierCeil. That rail is the only lever where the SHOWN
 * price and the AI's WILLINGNESS move together.
 *
 * No React, no hooks. Pure so the headless probes can import the same code the
 * browser runs — a hand-copied normalizer in the mirror is exactly the drift
 * the severance gate exists to catch.
 *
 * ── THE PROPERTY THAT MAKES THIS CORRECT ────────────────────────────
 * The board and the bid engine conserve differently:
 *
 *   - The BOARD renormalizes. usePricing rescales the whole map back to
 *     budget × teams, so raising one position deflates the rest ON SCREEN.
 *   - The BID SIDE does not. posMult scales tierFloor/tierCeil and stops.
 *     Raising QB adds willingness to the room without removing any.
 *
 * Ship that naively and the user sees a zero-sum reallocation while the room
 * becomes non-zero-sum and everything clears higher — "I moved the numbers and
 * prices went UP", the most confusing failure available.
 *
 * The fix is to normalize the vector to spend-weighted-neutral BEFORE either
 * side sees it, which is what normalizePositionMultipliers does.
 *
 * ── AND THE PROPERTY THAT MAKES THE LABEL HONEST ────────────────────
 * Normalization divides every entry by the same scalar, so it preserves RATIOS
 * exactly. RB +20% with the rest untouched normalizes to RB ×1.147 and the
 * other three ×0.956 — and 1.147 / 0.956 is exactly 1.20. That is why the UI
 * may say "RB 20% pricier RELATIVE TO other positions" and be describing
 * literally what the code did. It may NOT say "20% of spend": that claim is not
 * what this computes.
 */
import { REFERENCE_SPEND_SHARES } from './leagueMarket';

export const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

/**
 * The slider domain. Symmetric ±25%, which reads correctly in a UI.
 *
 * 🔴 THIS CLAMP IS THE ONLY ONE ON THE USER PATH. It is tempting to think the
 * range is safe because it sits inside leagueMarket's MULT_MIN 0.75 /
 * MULT_MAX 1.35 — it does, but that is a NUMERIC COINCIDENCE THIS FILE MUST
 * MAINTAIN BY HAND, not a guard rail it inherits. Those two constants are
 * module-private in leagueMarket.js (:115-116) and are applied at exactly one
 * site (:216), inside the league-HISTORY path. Nothing in that file will ever
 * see a user dial. Widen the range here and nothing downstream will stop you.
 */
export const POSITION_DIAL_RANGE = { min: 0.75, max: 1.25, step: 0.05, default: 1.0 };

export const DEFAULT_POSITION_DIALS = { QB: 1.0, RB: 1.0, WR: 1.0, TE: 1.0 };

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const clamp = (v) => Math.min(POSITION_DIAL_RANGE.max, Math.max(POSITION_DIAL_RANGE.min, v));

/** Every dial present, numeric, and exactly 1.0. */
function allDefault(mults) {
  return POSITIONS.every(pos => mults[pos] === 1);
}

/**
 * Divide the vector by its spend-weighted mean so it is neutral overall.
 *
 * Returns null when every input is exactly 1.0 — the returns-null-not-1
 * discipline `hotListMultiplier` uses. An untouched dial then skips the
 * multiply ENTIRELY rather than multiplying by 1.0, so the board stays
 * BIT-identical instead of merely equal after a float round trip. That is what
 * makes the inert gate meaningful.
 *
 * Returns null for garbage (missing keys, NaN, strings) rather than producing
 * NaN prices — a price map full of NaN would render as "$NaN" everywhere and
 * poison every bid.
 *
 * NOTE ON THE DIVISOR. REFERENCE_SPEND_SHARES sums to 0.97, not 1.0 (DEF and K
 * hold the rest and carry no multiplier). The divisor is Σ(share × mult) over
 * the four, so the output satisfies Σ(share × out) === Σ(share), i.e. the
 * weighted mean is preserved rather than forced to 1 — which is the condition
 * that leaves usePricing's renormalization `scale` where it was. Dividing by a
 * 0.97-weighted sum without accounting for that would inflate every price by
 * 1/0.97 ≈ 3%.
 */
export function normalizePositionMultipliers(mults) {
  if (!mults || typeof mults !== 'object') return null;
  for (const pos of POSITIONS) if (!isNum(mults[pos])) return null;
  if (allDefault(mults)) return null;

  let weighted = 0, shareTotal = 0;
  for (const pos of POSITIONS) {
    const share = REFERENCE_SPEND_SHARES[pos] || 0;
    weighted += share * mults[pos];
    shareTotal += share;
  }
  if (!(weighted > 0) || !(shareTotal > 0)) return null;

  // mean = the spend-weighted average multiplier. Dividing by it makes the
  // weighted average exactly 1 while leaving every ratio untouched.
  const mean = weighted / shareTotal;
  if (!isNum(mean) || mean === 0) return null;

  const out = {};
  for (const pos of POSITIONS) out[pos] = mults[pos] / mean;
  return out;
}

/**
 * Slider values in, the vector the pricing path uses out (or null when the
 * dials are untouched, which is the OFF path).
 */
export function buildUserPositionMultipliers(dials) {
  if (!dials || typeof dials !== 'object') return null;
  const clamped = {};
  for (const pos of POSITIONS) {
    if (!isNum(dials[pos])) return null;
    clamped[pos] = clamp(dials[pos]);
  }
  return normalizePositionMultipliers(clamped);
}

/**
 * The per-position effect, as percentages, for the UI readout.
 *
 * Chunk 3 renders THIS rather than restating the raw slider, so the number on
 * screen is the number the engine used. `{ RB: 14.7, WR: -4.4, ... }`.
 * Returns null on the OFF path so the readout can be absent rather than four
 * zeroes.
 */
export function describePositionDials(dials) {
  const out = buildUserPositionMultipliers(dials);
  if (!out) return null;
  const pct = {};
  for (const pos of POSITIONS) pct[pos] = (out[pos] - 1) * 100;
  return pct;
}

/** Shape-check a stored dial object, for the settings/league round trip. */
export function sanitizePositionDials(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_POSITION_DIALS };
  const out = {};
  for (const pos of POSITIONS) out[pos] = isNum(raw[pos]) ? clamp(raw[pos]) : 1.0;
  return out;
}

/** True when the dials are all at rest — the cheap check for "is this off". */
export function dialsAreDefault(dials) {
  return !buildUserPositionMultipliers(dials);
}
